import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Search, MoreVertical, Check, X, LayoutGrid, List, Mail, Loader2 } from "lucide-react";
import { SourceBadge, ScoreBandBadge } from "@/components/common/CandidateBadges";
import { useState, useEffect } from "react";
import { engineApiClient, apiRequest } from "@/lib/apiClient";
import { engineEndpoints } from "@/lib/contracts";
import type { CandidateListResponse } from "@/lib/contracts";

interface Candidate {
  id: string;
  name: string;
  title: string;
  company: string;
  matchScore: number;
  location: string;
  source?: string;
  scoreBand?: string;
  linkedinUrl?: string;
  preScreenStatus?: "not_started" | "scheduled" | "complete";
}

type Stage = "identified" | "outreach" | "shortlisted" | "interview" | "offer";

// Normalise the API pipeline_stage string to our Stage union
function toStage(raw?: string): Stage {
  const s = (raw ?? "identified").toLowerCase();
  if (s === "outreach")   return "outreach";
  if (s === "shortlisted") return "shortlisted";
  if (s === "interview")  return "interview";
  if (s === "offer")      return "offer";
  return "identified";
}

const EMPTY_STAGES: Record<Stage, Candidate[]> = {
  identified: [], outreach: [], shortlisted: [], interview: [], offer: [],
};

const stages: { id: Stage; label: string; color: string; bgColor: string }[] = [
  { id: "identified", label: "Identified", color: "text-gray-400", bgColor: "bg-gray-500/10" },
  { id: "outreach", label: "Outreach", color: "text-purple-400", bgColor: "bg-purple-500/10" },
  { id: "shortlisted", label: "Shortlisted", color: "text-blue-400", bgColor: "bg-blue-500/10" },
  { id: "interview", label: "Interview", color: "text-yellow-400", bgColor: "bg-yellow-500/10" },
  { id: "offer", label: "Offer", color: "text-green-400", bgColor: "bg-green-500/10" },
];

export function CandidatePipelineBoard() {
  const { id: projectId } = useParams<{ id: string }>();

  const [candidatesByStage, setCandidatesByStage] = useState<Record<Stage, Candidate[]>>(EMPTY_STAGES);
  const [isLoading, setIsLoading] = useState(true)

  // Fetch live candidates from the same API as the dashboard
  useEffect(() => {
    if (!projectId) return;
    setIsLoading(true);
    engineApiClient.listCandidates(projectId)
      .then((res: CandidateListResponse) => {
        const byStage: Record<Stage, Candidate[]> = { ...EMPTY_STAGES, identified: [], outreach: [], shortlisted: [], interview: [], offer: [] };
        res.candidates
          .filter((c) => !("rejected" in c && c.rejected)) // exclude rejected if present
          .forEach((c) => {
            const stage = toStage((c as unknown as { pipelineStage?: string }).pipelineStage);
            // Parse "Role, Company" title string from Apollo
            const rawTitle  = c.candidateTitle ?? '';
            const commaIdx  = rawTitle.indexOf(', ');
            const roleTitle = commaIdx > -1 ? rawTitle.slice(0, commaIdx) : rawTitle;
            const company   = commaIdx > -1 ? rawTitle.slice(commaIdx + 2) : '';
            // Use locationText (dedicated field) — fall back to summary regex for older rows
            const locationText  = (c as unknown as { locationText?: string }).locationText;
            const summaryText   = (c as unknown as { summary?: string }).summary ?? '';
            const locationMatch = !locationText ? summaryText.match(/^Based in ([^.]+)\./) : null;
            const location      = locationText ?? (locationMatch ? locationMatch[1] : '');
            byStage[stage].push({
              id:           c.candidateId,
              name:         c.candidateName,
              title:        roleTitle,
              company,
              matchScore:   c.fitScore,
              location,
              source:       c.source,
              scoreBand:    c.scoreBand,
              linkedinUrl:  c.linkedinUrl,
              preScreenStatus: "not_started",
            });
          });
        setCandidatesByStage(byStage);
      })
      .catch(() => { /* keep empty state on error */ })
      .finally(() => setIsLoading(false));
  }, [projectId]);

  const [draggedCandidate, setDraggedCandidate] = useState<{ candidate: Candidate; fromStage: Stage } | null>(null);

  const handleDragStart = (candidate: Candidate, stage: Stage) => {
    setDraggedCandidate({ candidate, fromStage: stage });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (toStage: Stage) => {
    if (!draggedCandidate) return;
    const { candidate, fromStage } = draggedCandidate;
    if (fromStage === toStage) { setDraggedCandidate(null); return; }
    // Optimistic update
    setCandidatesByStage((prev) => ({
      ...prev,
      [fromStage]: prev[fromStage].filter((c) => c.id !== candidate.id),
      [toStage]: [...prev[toStage], candidate],
    }));
    setDraggedCandidate(null);
    // Persist to backend
    if (projectId) {
      apiRequest({
        path: engineEndpoints.candidateStage(projectId, candidate.id),
        method: 'PATCH',
        body: { stage: toStage },
      }).catch(() => { /* optimistic — show correct state locally even if persist fails */ })
    }
  };

  const getTotalCandidates = () =>
    Object.values(candidatesByStage).reduce((sum, candidates) => sum + candidates.length, 0);

  const [sourceFilter, setSourceFilter] = useState<'all' | 'applied' | 'sourced' | 'uploaded'>('all');

  const allCandidates = Object.values(candidatesByStage).flat();
  const sourcedCount  = allCandidates.filter((c) => c.source === 'sourced').length;
  const appliedCount  = allCandidates.filter((c) => c.source === 'applied').length;
  const uploadedCount = allCandidates.filter((c) => !c.source || c.source === 'uploaded').length;

  const filterCandidate = (c: Candidate) =>
    sourceFilter === 'all' ? true : (c.source ?? 'uploaded') === sourceFilter;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#02182B] flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-400">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-sm">Loading pipeline…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#02182B]">
      <header className="border-b border-[#233D4C] bg-[#02182B] sticky top-0 z-20">
        <div className="max-w-[1920px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Link to={`/project/${projectId}/dashboard`} className="text-gray-400 hover:text-white transition-colors">
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="text-white text-xl font-semibold">Pipeline Board</h1>
                <p className="text-gray-400 text-sm">{getTotalCandidates()} candidates</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-[#233D4C] rounded-lg p-1 border border-[#2a4a5c]">
                <Link to={`/project/${projectId}/dashboard`} className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white rounded-md transition-colors text-sm font-medium">
                  <List size={16} />List
                </Link>
                <Link to={`/project/${projectId}/pipeline`} className="flex items-center gap-2 px-3 py-2 bg-[#FD802E] text-[#233D4C] rounded-md transition-colors text-sm font-medium">
                  <LayoutGrid size={16} />Board
                </Link>
              </div>
              <Link to={`/project/${projectId}/outreach`} className="flex items-center gap-2 px-4 py-2 bg-[#233D4C] text-white rounded-lg hover:bg-[#2a4a5c] transition-colors text-sm">
                <Mail size={16} />Outreach
              </Link>
              <button className="flex items-center gap-2 px-4 py-2 bg-[#233D4C] text-white rounded-lg hover:bg-[#2a4a5c] transition-colors text-sm">
                <Download size={16} />Export
              </button>
            </div>
          </div>
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
            <input
              type="text"
              placeholder="Search candidates..."
              className="w-full bg-[#233D4C] border border-[#2a4a5c] rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-[#FD802E] focus:ring-2 focus:ring-[#FD802E]/20 transition-colors text-sm"
            />
          </div>
        </div>
      </header>

      <div className="max-w-[1920px] mx-auto px-6 py-6">
        {/* Source filter tabs */}
        <div className="flex items-center gap-1 mb-5">
          {(
            [
              { key: 'all',      label: 'All',      count: getTotalCandidates() },
              { key: 'sourced',  label: 'Sourced',  count: sourcedCount },
              { key: 'applied',  label: 'Applied',  count: appliedCount },
              { key: 'uploaded', label: 'Uploaded', count: uploadedCount },
            ] as const
          ).map(({ key, label, count }) => (
            <button
              key={key}
              onClick={() => setSourceFilter(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                sourceFilter === key
                  ? 'bg-[#FD802E]/15 text-[#FD802E] border border-[#FD802E]/30'
                  : 'text-gray-400 hover:text-white hover:bg-[#233D4C]'
              }`}
            >
              {label}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                sourceFilter === key ? 'bg-[#FD802E]/20 text-[#FD802E]' : 'bg-[#233D4C] text-gray-500'
              }`}>
                {count}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-6 mb-6">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-sm">Total Candidates:</span>
            <span className="text-white font-bold text-lg">{getTotalCandidates()}</span>
          </div>
          <div className="h-4 w-px bg-[#233D4C]" />
          {stages.map((stage) => (
            <div key={stage.id} className="flex items-center gap-2">
              <span className={`text-sm ${stage.color}`}>{stage.label}:</span>
              <span className="text-white font-semibold">{candidatesByStage[stage.id].length}</span>
            </div>
          ))}
        </div>

        <div className="flex gap-4 overflow-x-auto pb-4">
          {stages.map((stage) => (
            <div
              key={stage.id}
              className="flex-shrink-0 w-80"
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(stage.id)}
            >
              <div className={`${stage.bgColor} rounded-t-lg border-2 border-b-0 p-4`}>
                <div className="flex items-center justify-between mb-1">
                  <h3 className={`font-semibold ${stage.color}`}>{stage.label}</h3>
                  <span className={`${stage.bgColor} ${stage.color} text-xs font-bold px-2 py-1 rounded-full`}>
                    {candidatesByStage[stage.id].filter(filterCandidate).length}
                  </span>
                </div>
              </div>

              <div className="bg-[#233D4C]/30 border-2 border-t-0 rounded-b-lg p-3 min-h-[600px]">
                <div className="space-y-3">
                  {candidatesByStage[stage.id].filter(filterCandidate).map((candidate) => (
                    <div
                      key={candidate.id}
                      draggable
                      onDragStart={() => handleDragStart(candidate, stage.id)}
                      className="bg-[#233D4C] rounded-lg border border-[#2a4a5c] p-4 hover:border-[#FD802E]/30 transition-all cursor-move group"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3 flex-1">
                          <div className="w-10 h-10 rounded-full bg-[#FD802E]/10 flex items-center justify-center text-[#FD802E] font-semibold flex-shrink-0 text-sm">
                            {candidate.name.split(" ").map((n) => n[0]).join("")}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <Link
                                to={`/project/${projectId}/candidate/${candidate.id}`}
                                className="text-white font-medium hover:text-[#FD802E] transition-colors truncate"
                              >
                                {candidate.name}
                              </Link>
                            </div>
                            <p className="text-gray-400 text-xs truncate mt-0.5">{candidate.title}</p>
                            {candidate.linkedinUrl && (
                              <a
                                href={candidate.linkedinUrl.startsWith('http') ? candidate.linkedinUrl : `https://${candidate.linkedinUrl}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-[11px] text-blue-400 hover:text-blue-300 hover:underline transition-colors mt-0.5 block"
                              >
                                View LinkedIn
                              </a>
                            )}
                          </div>
                        </div>
                        <button className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-[#02182B] rounded">
                          <MoreVertical size={16} className="text-gray-400" />
                        </button>
                      </div>

                      <div className="space-y-2 text-xs">
                        {candidate.company && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Company</span>
                          <span className="text-gray-300 truncate ml-2">{candidate.company}</span>
                        </div>
                        )}
                        {candidate.location && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Location</span>
                          <span className="text-gray-300">{candidate.location}</span>
                        </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Match</span>
                          <span className={`font-bold ${
                            candidate.matchScore >= 80 ? 'text-green-400' :
                            candidate.matchScore >= 65 ? 'text-[#FD802E]' :
                            'text-gray-400'
                          }`}>{candidate.matchScore}%</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Source</span>
                          <SourceBadge source={candidate.source ?? 'uploaded'} />
                        </div>
                        {candidate.scoreBand && (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Band</span>
                            <ScoreBandBadge band={candidate.scoreBand} />
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Pre-screen</span>
                          <span className={`text-xs font-medium ${
                            candidate.preScreenStatus === "complete"
                              ? "text-green-400"
                              : candidate.preScreenStatus === "scheduled"
                                ? "text-blue-400"
                                : "text-gray-600"
                          }`}>
                            {candidate.preScreenStatus === "complete"
                              ? "✓ Complete"
                              : candidate.preScreenStatus === "scheduled"
                                ? "Scheduled"
                                : "—"}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2 mt-3 pt-3 border-t border-[#2a4a5c]">
                        <button className="flex-1 flex items-center justify-center gap-1 bg-green-500/10 text-green-400 px-2 py-1.5 rounded text-xs hover:bg-green-500/20 transition-colors">
                          <Check size={14} />Approve
                        </button>
                        <button className="flex-1 flex items-center justify-center gap-1 bg-red-500/10 text-red-400 px-2 py-1.5 rounded text-xs hover:bg-red-500/20 transition-colors">
                          <X size={14} />Reject
                        </button>
                      </div>
                    </div>
                  ))}

                  {candidatesByStage[stage.id].length === 0 && (
                    <div className="text-center py-12">
                      <p className="text-gray-500 text-sm">No candidates yet</p>
                      <p className="text-gray-600 text-xs mt-1">Drag cards here</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
